import { useCallback, useEffect, useState } from 'react';

import { useHotelSorting } from 'hooks/form/search';
import { useResetChangedFilterList } from 'hooks/form/useChangedFilterList';
import { useOutsideClick } from 'hooks/useOutsideClick';

import { toggleValueInSet } from 'utility/Toggle/toggleValueInSet';

import { SortOrder } from 'types/Search/form';

import { ListItem } from '../types';

export const useLocationFilter = (
    list: ListItem[],
    currentFormSelection: Set<number>,
    setFormSelection: (selection: Set<number>) => void
) => {
    const resetChangedFilterList = useResetChangedFilterList();
    const [hotelSorting, setHotelSorting] = useHotelSorting();
    const buildValue = useCallback(
        (currentSelection: Set<number>) => {
            if (currentSelection.size === 0) {
                return '';
            }

            return list
                .filter((item) => currentSelection.has(item.id))
                .slice(0, 5)
                .map((item) => item.name)
                .join(', ');
        },
        [list]
    );
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(
        buildValue(currentFormSelection)
    );
    const [currentSelection, setCurrentSelection] = useState(
        new Set(currentFormSelection)
    );

    const isSelectionEmpty = currentSelection.size === 0;

    const onSelection = useCallback((id: number) => {
        setCurrentSelection((selection) => toggleValueInSet(selection, id));
    }, []);

    const clearSelection = useCallback(() => {
        setCurrentSelection(new Set());
    }, []);

    const onSubmit = useCallback(() => {
        setIsOpen(false);
        setInputValue(buildValue(currentSelection));
        setFormSelection(currentSelection);
        resetChangedFilterList();
    }, [
        buildValue,
        currentSelection,
        resetChangedFilterList,
        setFormSelection,
    ]);

    const closeOverlay = useCallback(() => {
        setIsOpen(false);
        setCurrentSelection(currentFormSelection);
        setInputValue(buildValue(currentSelection));
    }, [buildValue, currentFormSelection, currentSelection]);

    const overlayRef = useOutsideClick<HTMLDivElement>(closeOverlay);

    const openOverlay = useCallback(() => {
        setInputValue('');
        setIsOpen(true);
    }, []);

    useEffect(() => {
        if (isOpen) {
            return;
        }
        if (isSelectionEmpty && hotelSorting === SortOrder.DistanceCityCenter) {
            setHotelSorting(SortOrder.Popularity);
        }
        setCurrentSelection(currentFormSelection);
        setInputValue(buildValue(currentSelection));
    }, [
        buildValue,
        currentFormSelection,
        currentSelection,
        hotelSorting,
        isOpen,
        isSelectionEmpty,
        setHotelSorting,
    ]);

    return {
        isOpen,
        inputValue,
        currentSelection,
        isSelectionEmpty,
        overlayRef,
        setInputValue,
        onSelection,
        clearSelection,
        onSubmit,
        openOverlay,
    };
};
